import WalletPaymentService from './WalletPaymentService';
import RazorPayService from './RazorPayService';
import { supabase } from '../lib/supabase/client/supabaseClient';

interface PaymentResult {
    success: boolean;
    error?: string;
    payment_id?: string;
    order_id?: string;
    amount?: number;
    updatedFiles: Array<{
        emailInvoicePath: string;
        combined_file?: string;
        groupId?: string;
    }>;
}

interface SplitPaymentResponse {
    success: boolean;
    orderId?: string;
    walletAmount?: number;
    razorpayAmount?: number;
    updatedFiles?: Array<{
        emailInvoicePath: string;
        combined_file?: string;
        groupId?: string;
    }>;
}

class InsufficientWalletBalanceService {
    async handleSplitPayment(
        userId: string, 
        docId: string, 
        totalAmount: number, 
        walletBalance: number, 
        userInfo: any,
        paymentType: 'PRINT_BAZAAR' | 'ORDER',
        orderSpecificData?: any
    ): Promise<SplitPaymentResponse> {
        try {
            // Calculate remaining amount for Razorpay
            const remainingAmount = totalAmount - walletBalance;

            // Create Razorpay order
            const orderData = await RazorPayService.createOrderId(
                remainingAmount,
                paymentType,
                userId,
                'INR'
            );

            try {
                // Process payment based on type with proper type assertions
                const paymentResult = (paymentType === 'PRINT_BAZAAR' 
                    ? await RazorPayService.processPrintBazaarPayment(
                        orderData.orderId,
                        remainingAmount,
                        userId,
                        {
                            author_id: userInfo.author_id,
                            document_id: docId,
                            totalCost: remainingAmount
                        },
                        {
                            prefill: {
                                name: userInfo.name,
                                email: userInfo.email,
                            }
                        }
                    )
                    : await RazorPayService.processOrderPayment(
                        orderData.orderId,
                        remainingAmount,
                        userId,
                        {
                            prefill: {
                                name: userInfo.name,
                                email: userInfo.email,
                            }
                        },
                        orderSpecificData
                    )) as PaymentResult;

                if (paymentResult.success) {
                    // Deduct wallet balance after successful payment
                    const deductSuccess = await WalletPaymentService.deductWalletBalance(userId, walletBalance);
                    if (!deductSuccess) {
                        throw new Error('Failed to deduct wallet balance');
                    }
                    // Add wallet transaction record
                    await WalletPaymentService.addWalletTransaction(
                        userId, 
                        walletBalance, 
                        paymentType === 'PRINT_BAZAAR' ? 'print_bazaar_purchase' : 'print_order'
                    );

                    const response: SplitPaymentResponse = {
                        success: true,
                        orderId: orderData.orderId,
                        walletAmount: walletBalance,
                        razorpayAmount: remainingAmount,
                    };

                    // Add updatedFiles only for ORDER type payments
                    if (paymentType === 'ORDER' && paymentResult.updatedFiles) {
                        return {
                            ...response,
                            updatedFiles: paymentResult.updatedFiles
                        };
                    }

                    return response;
                }
                
                return { success: false };
            } catch (error) {
                throw error;
            }
        } catch (error) {
            console.error('Split payment failed:', error);
            return { success: false };
        }
    }

    private async rollbackWalletDeduction(userId: string, amount: number) {
        const { error } = await supabase
            .schema('api')
            .rpc('add_wallet_balance', {
                user_id_param: userId,
                amount_to_add: amount
            });

        if (error) {
            console.error('Failed to rollback wallet deduction:', error);
        }
    }
}

export default new InsufficientWalletBalanceService();
